import React, { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import _get from 'lodash/get';
import {
  Access,
  AccessComponentEnum,
  AccessLevelEnum,
  AiConversationFeatureEnum,
  UserRoleEnum,
  useAuthenticationSessionEndMutation,
  useDashboardTaskCounterQuery,
  useEdmDocumentationsCounterQuery,
} from 'graphql-common';
import { useApolloClient } from '@apollo/client/react';
import toast from '@lib/components/Toast/Toast';
import { getErrorMessage } from '@lib/utils/graphQLErrors';
import DashboardLayoutCommon from '@lib/layouts/DashboardLayout/DashboardLayout';
import {
  DebugModeProvider,
  logger,
  useNavigationLogger,
  usePlanTransitionLogger,
} from '@lib/services/logger';
import { GetUrlParams } from '@lib/enums/urls';
import useDeepStableValue from '@lib/hooks/useDeepStableValue';
import { useWhoamiContext } from 'hooks/useWhoamiContext';
import { useLocation, useParams } from 'react-router-dom';
import getNavItems, {
  getSettingsNavItems,
} from 'layouts/DashboardLayout/utils/getNavItems';
import getProfileMenuItems from 'layouts/DashboardLayout/utils/getProfileMenuItems';
import PlatformSwitcher, {
  Platforms,
} from '@lib/components/PlatformSwitcher/PlatformSwitcher';
import {
  checkCompanyPlanAccessByComponent,
  checkUserAccessByComponent,
  checkUserAccessForPage,
  checkUserAccessItemIdsByComponent,
} from '@lib/utils/accesses';
import getHomePageUrl from 'layouts/DashboardLayout/utils/getHomePageUrl';
import { useTechnicalPauseContext } from '@lib/contexts/TechnicalPauseContext';
import JobTracker from 'components/JobTracker/JobTracker';
import AiAssistantStickyBtn from 'components/AiAssistant/AiAssistantStickyBtn';
import AiBudgetProvider from 'components/AiAssistant/AiBudgetProvider';
import { getDocumentAiAssistantTexts } from 'components/AiAssistant';
import { APP_URLS } from 'constants/urls';

const { VITE_AUTH_PLATFORM_URL: AUTH_PLATFORM } = import.meta.env;

// Apollo's cache can't normalize these (their elements have no `id`), so it
// replaces the whole array on every whoami write — a fresh `[]` here would
// otherwise still be a new reference every render once defaulted.
const EMPTY_ACCESSES: Access[] = [];

function DashboardLayout(): React.ReactElement {
  const { t } = useTranslation();
  const apolloClient = useApolloClient();
  const { pathname, search } = useLocation();
  const { id = '', ...rawRestParams } = useParams();
  // `useParams`'s rest-spread is a new object every render regardless of
  // content — it's a dep of the `navItems` memo below, which would otherwise
  // recompute (and defeat DashboardSideBar's React.memo) on every unrelated
  // render.
  const restParams = useDeepStableValue(rawRestParams);
  const tabParam = new URLSearchParams(search).get(GetUrlParams.Tab);
  const { activeTechnicalPause, upcomingTechnicalPause } =
    useTechnicalPauseContext();

  // Reads ProtectedRoute's single whoami query result — must not run its own
  // independent `cache-only` query here (see useWhoamiContext for why).
  const { currentUser: rawCurrentUser } = useWhoamiContext();

  // `finalAccesses`/`company.plan.accesses` replacing wholesale (see
  // EMPTY_ACCESSES above) also replaces the parent `currentUser` object on
  // every whoami write, even when nothing actually changed (e.g. the
  // tab-focus refetch in ProtectedRoute returning identical data).
  // Stabilizing `currentUser` itself keeps every downstream
  // useCallback/useMemo — and the `user` passed to DashboardLayoutCommon,
  // which feeds contextProps — from cascading into a brand-new identity on
  // every such refetch. Deep-comparing the whole whoami payload here isn't
  // free, but at current payload size (tens of access records) it's well
  // under the cost that would matter on this render-per-navigation component.
  const currentUser = useDeepStableValue(rawCurrentUser);
  // Stabilizing `currentUser` above does NOT make these two redundant: some
  // whoami calls elsewhere in the app (e.g. UsersContainer's
  // `includeFullData: false` query) write sibling fields — like
  // `company.activeUsersCount` — on the same normalized User entity, which
  // still gives `currentUser` a new identity while `finalAccesses`/
  // `company.plan.accesses` themselves are untouched. These inner calls are
  // what keep the useCallbacks below stable across exactly that case.
  const finalAccesses =
    useDeepStableValue(currentUser?.finalAccesses) ?? EMPTY_ACCESSES;
  // Left undefined (not defaulted to EMPTY_ACCESSES) until whoami actually
  // resolves — usePlanTransitionLogger below treats an early `[]` as a real
  // "initial snapshot" and would otherwise log a bogus empty-plan entry
  // before the first real fetch completes.
  const companyPlanAccesses = useDeepStableValue(
    currentUser?.company?.plan?.accesses,
  );

  // Debug mode logging
  useNavigationLogger();
  usePlanTransitionLogger({
    accesses: companyPlanAccesses,
  });

  const getUserAccessByUrl = useCallback(
    (pathnameStr: string) => checkUserAccessForPage(finalAccesses, pathnameStr),
    [finalAccesses],
  );

  const getUserAccessByComponent = useCallback(
    (component: AccessComponentEnum) =>
      checkUserAccessByComponent(finalAccesses, component),
    [finalAccesses],
  );

  const getUserAccessItemIdsByComponent = useCallback(
    (component: AccessComponentEnum) =>
      checkUserAccessItemIdsByComponent(finalAccesses, component),
    [finalAccesses],
  );

  const getUserAccessByCompany = useCallback(
    (component: AccessComponentEnum) =>
      checkCompanyPlanAccessByComponent(
        companyPlanAccesses ?? EMPTY_ACCESSES,
        component,
      ),
    [companyPlanAccesses],
  );
  const isFullAdmin = currentUser?.role === UserRoleEnum.FullAdmin;
  const logoUrl = getHomePageUrl(finalAccesses);

  const resolveBreadcrumbLinkPath = useCallback(
    (path: string) =>
      getUserAccessByUrl(path) === AccessLevelEnum.LimitedRead ? logoUrl : path,
    [getUserAccessByUrl, logoUrl],
  );

  // MY_APPROVALS is a READ_SCOPE on the BE → TaskPolicy#scope? requires
  // :read on :tasks_management, so LimitedRead users get NotAuthorized
  // and the whole counter query errors out. Skip it for those users.
  // Tasks aren't plan-gated, so only the component-level access matters.
  const isTasksLocked =
    getUserAccessByComponent(AccessComponentEnum.TasksManagement) ===
    AccessLevelEnum.LimitedRead;
  const countersQuery = useDashboardTaskCounterQuery({
    skip: isTasksLocked,
  });
  const myApprovalsCounter = _get(
    countersQuery,
    'data.myApprovals.metadata.totalCount',
    0,
  );

  // Mirror of the task-approvals counter for the Documentation > Approvals
  // nav item. Skipped when DocumentationManagement is plan-locked to avoid
  // BE authorization errors (premium fields like approverId are gated).
  const isDocumentationLocked =
    getUserAccessByCompany(AccessComponentEnum.DocumentationManagement) ===
      AccessLevelEnum.LimitedRead ||
    getUserAccessByComponent(AccessComponentEnum.DocumentationManagement) ===
      AccessLevelEnum.LimitedRead;
  const documentationApprovalsCounterQuery = useEdmDocumentationsCounterQuery({
    variables: {
      filters: currentUser?.id
        ? [{ approverId: { predicate: { eq: currentUser.id } } }]
        : undefined,
    },
    skip: isDocumentationLocked || !currentUser?.id,
  });
  const myDocumentationApprovalsCounter = _get(
    documentationApprovalsCounterQuery,
    'data.myApprovals.metadata.totalCount',
    0,
  );

  const callbackOnLogout = async () => {
    localStorage.clear();
    window.location.href = AUTH_PLATFORM;
  };

  const [logoutMutation, logoutMutationResult] =
    useAuthenticationSessionEndMutation({
      onCompleted: () => {
        apolloClient.clearStore().then(callbackOnLogout);
      },
      onError: (error) => {
        toast({ content: getErrorMessage(error, t) });
        callbackOnLogout();
      },
    });

  const onLogoutHandler = useCallback(
    () => logoutMutation({ variables: { input: {} } }),
    [logoutMutation],
  );

  const navItems = useMemo(
    () =>
      getNavItems({
        getUserAccessByCompany,
        getUserAccessByComponent,
        id,
        isFullAdmin,
        myApprovalsCounter,
        myDocumentationApprovalsCounter,
        pathname,
        t,
        urlParams: restParams,
      }),
    [
      getUserAccessByCompany,
      getUserAccessByComponent,
      id,
      isFullAdmin,
      myApprovalsCounter,
      myDocumentationApprovalsCounter,
      pathname,
      t,
      restParams,
    ],
  );

  const settingsMenuNavItems = useMemo(
    () =>
      getSettingsNavItems({
        getUserAccessByCompany,
        getUserAccessByComponent,
        id,
        isFullAdmin,
        pathname,
        t,
      }),
    [
      getUserAccessByCompany,
      getUserAccessByComponent,
      id,
      isFullAdmin,
      pathname,
      t,
    ],
  );

  const profileMenuItems = useMemo(
    () =>
      getProfileMenuItems({
        logoutLoading: logoutMutationResult.loading,
        onLogoutHandler,
        t,
      }),
    [logoutMutationResult.loading, onLogoutHandler, t],
  );

  const outletContext = useMemo(
    () => ({
      getUserAccessByCompany,
      getUserAccessByComponent,
      getUserAccessByUrl,
      getUserAccessItemIdsByComponent,
      isFullAdmin,
      logoutLoading: logoutMutationResult.loading,
      onLogoutHandler,
    }),
    [
      getUserAccessByCompany,
      getUserAccessByComponent,
      getUserAccessByUrl,
      getUserAccessItemIdsByComponent,
      isFullAdmin,
      logoutMutationResult.loading,
      onLogoutHandler,
    ],
  );

  const tools = useMemo(
    () => (
      <PlatformSwitcher selected={Platforms.Admin} available={Platforms.User} />
    ),
    [],
  );

  const settingsPaths = useMemo(
    () => [
      APP_URLS.dashboard.publicApi.path,
      APP_URLS.dashboard.dataImport.path,
      APP_URLS.dashboard.formChecklists.add.path,
      APP_URLS.dashboard.formChecklists.edit.getDynamicPath({
        pathParts: { id },
      }),
      APP_URLS.dashboard.sso.add.path,
      APP_URLS.dashboard.sso.view.getDynamicPath({
        pathParts: { id },
      }),
      APP_URLS.dashboard.sso.edit.getDynamicPath({
        pathParts: { id },
      }),
      APP_URLS.dashboard.companyDomains.index.path,
    ],
    [id],
  );

  const disableContentScroll = useMemo(
    () =>
      APP_URLS.dashboard.formTemplates.editVersion.isTheSameUrlAs(pathname, {
        pathParts: { id, versionId: restParams?.versionId },
      }) ||
      APP_URLS.dashboard.formTemplates.add.isTheSameUrlAs(pathname) ||
      APP_URLS.dashboard.formTemplates.duplicate.isTheSameUrlAs(pathname, {
        pathParts: { id, versionId: restParams?.versionId },
      }) ||
      APP_URLS.dashboard.dataImport.isTheSameUrlAs(pathname) ||
      APP_URLS.dashboard.interventions.view.isTheSameUrlAs(pathname, {
        pathParts: { id },
      }) ||
      APP_URLS.dashboard.interventions.viewActions.isTheSameUrlAs(pathname, {
        pathParts: { id },
      }) ||
      APP_URLS.dashboard.assets.counters.isTheSameUrlAs(pathname) ||
      APP_URLS.dashboard.homeWidgets.isTheSameUrlAs(pathname) ||
      (APP_URLS.dashboard.documentation.view.isTheSameUrlAs(pathname, {
        pathParts: { id },
      }) &&
        tabParam !== 'operationalScope'),
    [pathname, id, restParams?.versionId, tabParam],
  );

  // The AI assistant floating button is scoped to documentation routes and
  // only shown when the AI assistance module is allowed on the company plan
  // AND the current user/team isn't restricted from it individually.
  const documentationPath = APP_URLS.dashboard.documentation.index.path;
  const isDocumentationRoute =
    pathname === documentationPath ||
    pathname.startsWith(`${documentationPath}/`);
  const companyAiAccess = getUserAccessByCompany(
    AccessComponentEnum.AiAssistance,
  );
  const userAiAccess = getUserAccessByComponent(
    AccessComponentEnum.AiAssistance,
  );
  const isAiAssistantAllowed =
    companyAiAccess === AccessLevelEnum.Write &&
    userAiAccess === AccessLevelEnum.Write;
  const showAiAssistantStickyBtn =
    isDocumentationRoute && isAiAssistantAllowed && !!currentUser;

  useEffect(() => {
    logger.debug('AI assistant: visibility computed', {
      'aiAssistant.pathname': pathname,
      'aiAssistant.isDocumentationRoute': isDocumentationRoute,
      'aiAssistant.companyAccess': companyAiAccess ?? 'none',
      'aiAssistant.userAccess': userAiAccess ?? 'none',
      'aiAssistant.hasCurrentUser': !!currentUser,
      'aiAssistant.visible': showAiAssistantStickyBtn,
    });
  }, [
    pathname,
    isDocumentationRoute,
    companyAiAccess,
    userAiAccess,
    currentUser,
    showAiAssistantStickyBtn,
  ]);

  return (
    <DebugModeProvider
      debugModeUntil={currentUser?.debugModeUntil}
      userId={currentUser?.id}
      userEmail={currentUser?.email}
    >
      {/* One AI budget for the whole dashboard: the chat, the per-field
          autofill affordances and the import form all gate on the same live
          `blockedReason`, and a generation on any of them refreshes it for
          the rest. */}
      <AiBudgetProvider
        enabled={
          isAiAssistantAllowed && !isDocumentationLocked && !!currentUser
        }
      >
        {showAiAssistantStickyBtn && (
          <AiAssistantStickyBtn
            user={currentUser}
            feature={AiConversationFeatureEnum.Documentation}
            texts={getDocumentAiAssistantTexts(t)}
            getUserAccessByCompany={getUserAccessByCompany}
          />
        )}
        <DashboardLayoutCommon
          activeTechnicalPause={activeTechnicalPause}
          disableContentScroll={disableContentScroll}
          resolveBreadcrumbLinkPath={resolveBreadcrumbLinkPath}
          logoUrl={logoUrl}
          navItems={navItems}
          outletContext={outletContext}
          profileMenuItems={profileMenuItems}
          profileUrl={APP_URLS.dashboard.profile.path}
          settingsMenuNavItems={settingsMenuNavItems}
          settingsPaths={settingsPaths}
          showPlatformSwitcher
          tools={tools}
          topComponents={<JobTracker />}
          upcomingTechnicalPause={upcomingTechnicalPause}
          user={currentUser}
        />
      </AiBudgetProvider>
    </DebugModeProvider>
  );
}

export default React.memo(DashboardLayout);
